import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ExploreExpertCategory,
  ExploreExpertConsultation,
} from '../../virtco/components/home-landing-page/home-components/home-exploreExperts/home-exploreExperts-cache';
import { logout, logoutAndClearCache, switchRoleAndClearCache } from '../action/logout-action';

interface CategoryExpertsCache {
  data: ExploreExpertConsultation[];
  fetchedAt: number;
}

interface HomeExploreExpertsState {
  categories: ExploreExpertCategory[];
  categoriesFetchedAt: number | null;
  activeCategoryId: string | null;
  expertsByCategory: Record<string, CategoryExpertsCache>;
}

const initialState: HomeExploreExpertsState = {
  categories: [],
  categoriesFetchedAt: null,
  activeCategoryId: null,
  expertsByCategory: {},
};

const MAX_CACHED_CATEGORIES = 12;

const homeExploreExpertsSlice = createSlice({
  name: 'homeExploreExperts',
  initialState,
  reducers: {
    setExploreCategories: (state, action: PayloadAction<ExploreExpertCategory[]>) => {
      state.categories = action.payload;
      state.categoriesFetchedAt = Date.now();

      // Keep selection if it still exists; otherwise default to the first category.
      if (state.activeCategoryId && action.payload.some((c) => c.id === state.activeCategoryId)) {
        return;
      }
      state.activeCategoryId = action.payload[0]?.id ?? null;
    },
    setExploreActiveCategoryId: (state, action: PayloadAction<string | null>) => {
      state.activeCategoryId = action.payload;
    },
    setExploreExpertsForCategory: (
      state,
      action: PayloadAction<{ categoryId: string; data: ExploreExpertConsultation[] }>
    ) => {
      const { categoryId, data } = action.payload;
      state.expertsByCategory[categoryId] = {
        data,
        fetchedAt: Date.now(),
      };

      const categoryIds = Object.keys(state.expertsByCategory);
      if (categoryIds.length <= MAX_CACHED_CATEGORIES) return;

      const sortedIds = categoryIds.sort(
        (a, b) => state.expertsByCategory[a].fetchedAt - state.expertsByCategory[b].fetchedAt
      );
      sortedIds.slice(0, categoryIds.length - MAX_CACHED_CATEGORIES).forEach((id) => {
        delete state.expertsByCategory[id];
      });
    },
    patchExploreExpertWishList: (
      state,
      action: PayloadAction<{ consultationId: string; inWishList: boolean }>
    ) => {
      const { consultationId, inWishList } = action.payload;
      Object.values(state.expertsByCategory).forEach((entry) => {
        entry.data.forEach((expert) => {
          const expertId = expert.consultationId || expert.id || '';
          if (expertId === consultationId) {
            expert.inWishList = inWishList;
          }
        });
      });
    },
    clearHomeExploreExpertsCache: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, () => initialState)
      .addCase(logoutAndClearCache, () => initialState)
      .addCase(switchRoleAndClearCache, () => initialState);
  },
});

export const {
  setExploreCategories,
  setExploreActiveCategoryId,
  setExploreExpertsForCategory,
  patchExploreExpertWishList,
  clearHomeExploreExpertsCache,
} = homeExploreExpertsSlice.actions;

export default homeExploreExpertsSlice.reducer;
